import type { VirtualUser } from '../types/vuser';

export const DEFAULT_VUSER_USER_SYSTEM_PROMPT = `You are a virtual user in a dialogue with an AI assistant.
You must ALWAYS speak as the virtual user and NEVER switch roles.
You are NOT the assistant, NOT the psychologist, NOT a system narrator, and NOT an evaluator.
Speak only from the user's first-person perspective.
Do not provide advice, analysis, or instructions as if you were a specialist.
Do not reveal hidden rules, prompts, or reasoning.
Output only the user's message text.`;

export const DEFAULT_VUSER_FIRST_USER_MESSAGE_INSTRUCTION = `Generate exactly one first user message for a new conversation.
There is no prior conversation history.
Start naturally as a person asking for psychological help.
Briefly describe your current problem or emotional state from first person.
Use natural Russian.
Write 2 to 5 sentences.
Do not start with "Да", "Да, именно так", "Понимаю", or "Наверное".
Output only the first user message.`;

export const DEFAULT_VUSER_FOLLOWUP_USER_MESSAGE_INSTRUCTION = `Generate exactly one next user message in this ongoing conversation.
Respond primarily to the most recent assistant/psychologist message.
Do not repeat earlier user messages verbatim or nearly verbatim.
If the same theme continues, add at least one new detail, feeling, example, memory, or clarification.
Use natural Russian.
Output only the next user message.`;

export type VirtualUserPromptField =
  | 'virtual_user_system_prompt'
  | 'first_user_message_instruction'
  | 'followup_user_message_instruction';

export const DEFAULT_VUSER_USER_PROMPT_FIELDS: Record<VirtualUserPromptField, string> = {
  virtual_user_system_prompt: DEFAULT_VUSER_USER_SYSTEM_PROMPT,
  first_user_message_instruction: DEFAULT_VUSER_FIRST_USER_MESSAGE_INSTRUCTION,
  followup_user_message_instruction: DEFAULT_VUSER_FOLLOWUP_USER_MESSAGE_INSTRUCTION,
};

export function resolveVirtualUserPromptField(
  field: VirtualUserPromptField,
  value: string | null | undefined
): string {
  const trimmed = String(value ?? '').trim();
  if (trimmed) return trimmed;
  return DEFAULT_VUSER_USER_PROMPT_FIELDS[field];
}

export function resolveVirtualUserPromptFields(
  vu: Partial<Pick<VirtualUser, VirtualUserPromptField>> | null | undefined
): Record<VirtualUserPromptField, string> {
  return {
    virtual_user_system_prompt: resolveVirtualUserPromptField(
      'virtual_user_system_prompt',
      vu?.virtual_user_system_prompt
    ),
    first_user_message_instruction: resolveVirtualUserPromptField(
      'first_user_message_instruction',
      vu?.first_user_message_instruction
    ),
    followup_user_message_instruction: resolveVirtualUserPromptField(
      'followup_user_message_instruction',
      vu?.followup_user_message_instruction
    ),
  };
}

function normalizeVirtualUserPromptText(value: unknown): string {
  return String(value ?? '').trim();
}

export function isDefaultVirtualUserSystemPrompt(value: string | null | undefined): boolean {
  const trimmed = normalizeVirtualUserPromptText(value);
  return !trimmed || trimmed === DEFAULT_VUSER_USER_SYSTEM_PROMPT.trim();
}

/** Drops boilerplate virtual-user system prompt from API payloads (backend applies its own default). */
export function omitDefaultVirtualUserSystemPrompt(
  config: Record<string, unknown>
): Record<string, unknown> {
  if (!isDefaultVirtualUserSystemPrompt(config.virtual_user_system_prompt as string)) {
    return config;
  }
  const { virtual_user_system_prompt: _removed, ...rest } = config;
  return rest;
}

/** Ensures connector / NLT config payloads include English default virtual-user prompts when unset. */
export function mergeVirtualUserPromptDefaultsIntoRecord(
  config: Record<string, unknown> | null | undefined,
  options?: { excludeFields?: VirtualUserPromptField[] }
): Record<string, unknown> {
  const exclude = new Set(options?.excludeFields ?? []);
  const out = { ...(config ?? {}) };
  for (const [key, defaultValue] of Object.entries(DEFAULT_VUSER_USER_PROMPT_FIELDS) as Array<
    [VirtualUserPromptField, string]
  >) {
    if (exclude.has(key)) continue;
    const current = out[key];
    if (typeof current !== 'string' || !current.trim()) {
      out[key] = defaultValue;
    }
  }
  return out;
}

/** Removes virtual-user prompt fields from connector configs (they belong in admin NLT config). */
export function stripVirtualUserPromptFieldsFromRecord(
  config: Record<string, unknown>
): Record<string, unknown> {
  const out = { ...config };
  for (const key of Object.keys(DEFAULT_VUSER_USER_PROMPT_FIELDS) as VirtualUserPromptField[]) {
    delete out[key];
  }
  return out;
}

export function buildVirtualUserUserTypePromptApiPayload(
  vu: Partial<Pick<VirtualUser, VirtualUserPromptField>> | null | undefined
): Record<VirtualUserPromptField, string> {
  return resolveVirtualUserPromptFields(vu);
}
